import { JellyfinItem } from "../api/jellyfin";

export type AnimeLocalization = "DUB" | "SUB" | undefined;

const DUB_TAGS = new Set(["dub", "dubbed", "english dub", "english dubbed"]);
const SUB_TAGS = new Set(["sub", "subbed", "subtitle", "subtitles", "fansub"]);

function normalized(value: string) {
  return value.trim().toLowerCase().replace(/[_.-]+/g, " ").replace(/\s+/g, " ");
}

function isAnime(item: JellyfinItem) {
  const providerIds = Object.keys(item.ProviderIds ?? {}).map(normalized);
  if (providerIds.some((id) => id === "anidb" || id === "mal" || id === "myanimelist")) return true;
  return [...(item.Genres ?? []), ...(item.Tags ?? [])]
    .map(normalized)
    .some((tag) => tag === "anime" || tag === "japanese animation");
}

/**
 * Resolves only explicit localization metadata. Track languages are used as
 * an observation when Jellyfin has already returned a probe, never as a
 * general filename/title heuristic.
 */
export function getAnimeLocalization(item: JellyfinItem): AnimeLocalization {
  if (!isAnime(item)) return undefined;
  const tags = (item.Tags ?? []).map(normalized);
  if (tags.some((tag) => DUB_TAGS.has(tag) || /(^|:)dub(?:bed)?$/.test(tag))) return "DUB";
  if (tags.some((tag) => SUB_TAGS.has(tag) || /(^|:)sub(?:bed)?$/.test(tag))) return "SUB";

  const streams = (item.MediaSources ?? []).flatMap((source) => source.MediaStreams ?? []);
  if (!streams.length) return undefined;
  const audioLanguages = streams
    .filter((stream) => stream.Type?.toLowerCase() === "audio" && stream.Language)
    .map((stream) => stream.Language!.toLowerCase());
  const hasEnglishAudio = audioLanguages.some((language) => language.startsWith("en"));
  const hasNonEnglishAudio = audioLanguages.some((language) => !language.startsWith("en"));
  const hasSubtitle = streams.some((stream) => stream.Type?.toLowerCase() === "subtitle");
  return hasEnglishAudio && hasNonEnglishAudio ? "DUB" : hasSubtitle && hasNonEnglishAudio ? "SUB" : undefined;
}
